import React, { useState, useEffect } from "react";
import {
  Stack,
  Typography,
  Modal,
  Box,
  IconButton,
  Button,
  useTheme,
  DialogTitle,
  Divider,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useSessionContext } from "../../../contexts/SessionContext";
import { useFormatter } from "../../../utils/useFormatter";
import { marketplaceService } from "../../../services/marketplace/MarketplaceService";
import { PrimaryButton } from "@/components/common";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import axios from "axios";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CommonDialog } from "@/components/common/CommonDialog";
import { FormButtonGroup } from "@/components/form";
import { useContractContext } from "@/contexts/ContractContext";

/**
 * Interface de propriedades para o MarketplaceCard
 */
interface MarketplaceCardProps {
  /** Título do card */
  title: string;
  /** Descrição da extensão */
  description: string;
  /** Status da extensão (em desenvolvimento, disponível, etc) */
  status: string;
  /** Função a ser chamada quando o usuário clicar em "Obter Extensão" */
  onActivateCheckout?: () => void;
  /** Informação de preço a ser exibida no canto superior esquerdo */
  priceInfo?: string;
}

/**
 * Componente MarketplaceCard
 *
 * Este componente exibe um card na página de extensões do Fluxmed com a opção
 * de ativar a integração com o Mercado Pago para o Marketplace Fluxmed.
 * A extensão é baseada em taxa de uso, sem necessidade de assinatura mensal.
 */
export function MarketplaceCard({
  title,
  description,
  status,
  onActivateCheckout,
  priceInfo,
}: MarketplaceCardProps) {
  const theme = useTheme();
  // Obtém os dados do usuário logado
  const { user } = useSessionContext();
  const { organizationId } = useOrganizationContext();
  const { currentContract } = useContractContext();

  const [loading, setLoading] = useState(false);
  const [hasExtension, setHasExtension] = useState(false);
  const [contractId, setContractId] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const { contractFluxPay, refetchExtensionStatus, additionalInformation } =
    useFluxPayContractContext();

  const nome = additionalInformation?.nome || "";
  const cpf = additionalInformation?.cpfCnpj || "";
  const email = additionalInformation?.email || "";
  /**
   * Verifica se a organização possui extensão marketplace ativa
   */
  useEffect(() => {
    const checkExtensionStatus = async () => {
      if (!organizationId) return;

      try {
        setCheckingStatus(true);
        // Obtém os dados da extensão marketplace
        const extensionData =
          await marketplaceService.checkMarketplaceExtension(organizationId);

        // Atualiza o estado com as informações recebidas da API
        setHasExtension(extensionData.hasActiveExtension);

        // Se a extensão estiver ativa, salva o ID do contrato
        if (extensionData.hasActiveExtension && extensionData.contractId) {
          setContractId(extensionData.contractId);
        }
      } catch (error) {
        console.error(
          "Erro ao verificar status da extensão marketplace:",
          error,
        );
      } finally {
        setCheckingStatus(false);
      }
    };

    checkExtensionStatus();
  }, [organizationId]);

  /**
   * Exibe uma mensagem de alerta para o usuário
   * @param message Mensagem de erro a ser exibida
   */
  const showErrorMessage = (message: string) => {
    alert(message); // Usando alert nativo ao invés de toast
  };

  /**
   * Exibe uma mensagem de sucesso em um modal para o usuário
   * @param message Mensagem de sucesso a ser exibida
   */
  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setSuccessModalOpen(true);
  };

  /**
   * Modal de sucesso com ícone e mensagem
   */
  const SuccessModal = () => (
    <Modal
      open={successModalOpen}
      onClose={() => setSuccessModalOpen(false)}
      aria-labelledby="success-modal"
      aria-describedby="success-message"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          textAlign: "center",
        }}
      >
        <CheckCircleIcon
          sx={{
            color: "success.main",
            fontSize: 60,
            mb: 2,
          }}
        />
        <Typography variant="h6" component="h2">
          {successMessage}
        </Typography>
        <IconButton
          onClick={() => setSuccessModalOpen(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          ×
        </IconButton>
      </Box>
    </Modal>
  );

  /**
   * Redireciona o usuário para a página de autorização do Mercado Pago
   * Utiliza as variáveis de ambiente para construir a URL de redirecionamento
   */
  const handleActivateMarketplace = async () => {
    try {
      setLoading(true);
      // Gera um ID de estado único para segurança contra CSRF
      const stateId = uuidv4();

      // Validação prévia do CPF
      if (!user?.username || user.username.length < 11) {
        console.error("CPF/CNPJ inválido", { username: user?.username });
        // Exibe mensagem de erro para o usuário
        showErrorMessage(
          "CPF/CNPJ inválido. Verifique seus dados de cadastro.",
        );
        return;
      }

      // Verifica se o organizationId está no formato esperado
      if (!organizationId) {
        console.error("ID da organização não encontrado", { organizationId });
        showErrorMessage(
          "Erro de configuração: ID da organização não encontrado.",
        );
        return;
      }

      // Verifica se é um CNPJ válido (apenas dígitos)
      if (!/^\d{8,14}$/.test(organizationId)) {
        console.error(
          "ID da organização em formato inválido (deve ser um CNPJ)",
          { organizationId },
        );
        showErrorMessage(
          "Erro de configuração: CNPJ da organização em formato inválido.",
        );
        return;
      }

      // Log dos dados que serão enviados ao servidor
      console.log("Dados de pré-autorização:", {
        cpf: user?.username,
        organizationId,
        stateId,
      });

      // Log das variáveis de ambiente
      console.log("Variáveis de ambiente:", {
        redirectUri: import.meta.env.VITE_MP_REDIRECT_SPLIT,
        mpClientId: import.meta.env.VITE_MP_CLIENT_ID_SPLIT,
      });

      // Registra a intenção de autorização no backend antes do redirecionamento
      await marketplaceService.registerPreAuthorization({
        cpf: user?.username || "",
        organizationId: organizationId,
        stateId,
        authType: "extension",
      });

      // Constrói a URL de redirecionamento para o Mercado Pago
      const redirectUri = import.meta.env.VITE_MP_REDIRECT_SPLIT;
      const mpClientId = import.meta.env.VITE_MP_CLIENT_ID_SPLIT;

      // Verifica se as variáveis de ambiente estão definidas
      if (!redirectUri || !mpClientId) {
        console.error("Variáveis de ambiente não configuradas corretamente", {
          redirectUri,
          mpClientId,
        });
        showErrorMessage(
          "Erro de configuração: parâmetros de integração não definidos.",
        );
        return;
      }

      // URL completa para autorização no Mercado Pago
      const authorizationUrl = `https://auth.mercadopago.com/authorization?client_id=${mpClientId}&response_type=code&platform_id=mp&redirect_uri=${encodeURIComponent(
        redirectUri,
      )}&state=${stateId}`;

      console.log("URL de redirecionamento:", authorizationUrl);

      // Redireciona o usuário para o Mercado Pago
      window.location.href = authorizationUrl;
    } catch (error: any) {
      console.error("Erro ao iniciar processo de autorização:", error);

      // Tratamento de erros mais detalhado
      if (error && error.isAxiosError) {
        console.error("Detalhes do erro da API:", {
          status: error.response?.status,
          data: error.response?.data,
        });

        if (error.response?.data?.message) {
          showErrorMessage(`Erro: ${error.response.data.message}`);
        } else {
          showErrorMessage(
            "Erro na comunicação com o servidor. Tente novamente mais tarde.",
          );
        }
      } else {
        showErrorMessage(
          "Ocorreu um erro inesperado. Tente novamente mais tarde.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abre o diálogo de confirmação de cancelamento
   */
  const handleOpenCancelDialog = () => {
    setShowCancelConfirmation(true);
  };

  /**
   * Fecha o diálogo de confirmação de cancelamento
   */
  const handleCloseCancelDialog = () => {
    setShowCancelConfirmation(false);
  };

  /**
   * Cancela o contrato da extensão marketplace após confirmação
   */
  const handleConfirmCancel = async () => {
    handleCloseCancelDialog();
    if (!contractId) {
      showErrorMessage("ID do contrato não disponível.");
      return;
    }

    try {
      setLoading(true);
      const result =
        await marketplaceService.cancelMarketplaceContract(contractId);

      if (result.success) {
        showSuccessMessage(result.message);
        setHasExtension(false);
        setContractId("");
        refetchExtensionStatus();
      } else {
        showErrorMessage("Falha ao cancelar contrato da extensão Fluxpay.");
      }
    } catch (error: any) {
      console.error("Erro ao cancelar contrato da extensão Fluxpay:", error);

      if (error.message) {
        showErrorMessage(error.message);
      } else {
        showErrorMessage(
          "Ocorreu um erro inesperado ao cancelar o contrato. Tente novamente mais tarde.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const { access } = useSessionContext();

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}

        <Stack
          sx={{
            position: "absolute",
            top: "12px",
            right: "0px",
            bgcolor:
              status === "Em desenvolvimento" ? "warning.main" : "success.main",
            color: "white",
            padding: "4px 12px",
            borderTopLeftRadius: "15px",
            borderBottomLeftRadius: "15px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1,
          }}
        >
          {status}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 1,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            {description}
          </Typography>

          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 1,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            É necessário que sua clínica possua uma conta de Pessoa Jurídica
            ativa no Mercado Pago.{" "}
            <a
              href="https://conteudo.mercadopago.com.br/conta-pj-do-mercado-pago"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Veja como criar sua conta pessoa jurídica no MercadoPago
            </a>
            .
          </Typography>

          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 1,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            Quanto custa utilizar o FluxPay:{" "}
            <a
              href="https://www.mercadopago.com.br/ajuda/33392"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Confira as taxas de link de pagamentos do Mercado Pago
            </a>
            . Além das taxas de transação do Mercado Pago, você pagará R$ 3,50
            por consulta à Fluxmed.
          </Typography>

          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 1,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            <a
              href="https://projectcore.atlassian.net/wiki/spaces/MDUP/pages/2135785474/FluxPay"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Saiba mais sobre o FluxPay.
            </a>
          </Typography>

          {contractFluxPay && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body1"
                fontWeight="600"
                textAlign="center"
                gutterBottom
              >
                Responsável pela contratação
              </Typography>
              <Box
                sx={{
                  backgroundColor: "neutral100.main",
                  p: 2,
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ mb: 1, wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Nome:</strong> {nome || "-"}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
                  <strong>{cpf?.length === 11 ? "CPF" : "CNPJ"}:</strong>{" "}
                  {cpf?.length === 11
                    ? useFormatter?.formatCPF(cpf)
                    : useFormatter?.formatCNPJ(cpf) || "-"}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Email:</strong> {email || "-"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {checkingStatus ? (
            <PrimaryButton
              width="60%"
              height="100%"
              disabled={true}
              onClick={() => {}}
            >
              Verificando status...
            </PrimaryButton>
          ) : hasExtension ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: access?.roles?.includes("owner")
                  ? "error700.main"
                  : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: access?.roles?.includes("owner")
                    ? "error700.main"
                    : "neutral300.main",
                },
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              onClick={handleOpenCancelDialog}
              disabled={!access.roles?.includes("owner") || loading}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
            >
              {access?.roles?.includes("owner")
                ? loading
                  ? "Carregando..."
                  : "Cancelar Fluxpay"
                : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={handleActivateMarketplace}
              disabled={!access.roles?.includes("owner") || loading}
              startIcon={<span style={{ color: "white" }}>💰</span>}
            >
              {access?.roles?.includes("owner")
                ? loading
                  ? "Carregando..."
                  : "Ativar FluxPay"
                : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>
      <SuccessModal />
      <CommonDialog
        isOpen={showCancelConfirmation}
        handleClose={handleCloseCancelDialog}
      >
        <DialogTitle align="center">
          Confirmar cancelamento da extensão FluxPay
        </DialogTitle>
        <Typography variant="subtitle2" fontWeight="500" align="center" mb={2}>
          Após a confirmação, o FluxPay será cancelado e os recursos adicionais
          não estarão mais disponíveis.
        </Typography>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <FormButtonGroup
            onGoBackButtonClick={handleCloseCancelDialog}
            onNextButtonClick={handleConfirmCancel}
            nextButtonText="Confirmar"
            goBackButtonText="Fechar"
          />
        </Stack>
      </CommonDialog>
    </>
  );
}
